import React, { ReactElement } from 'react';
import { DropdownButton, MenuItemProps } from '@odin-labs/components';
import moment, { Moment } from 'moment';
import { WeatherConditions } from 'apollo/generated/client-operations';
import { getDateTimeMoment, getJobsiteMomentNow } from 'utils/dates';
import { cn, momentISODateFormatter, useBoolean } from 'utils';
import { ChevronRightIcon } from 'components/icons';
import { Shimmer } from 'containers/jobsite/tabs/jobsiteOverview/components/JobsiteOverviewStates';
import { JobsiteOverviewWeatherDrawer } from './JobsiteOverviewWeatherDrawer';

export type WeatherCardConditions = Pick<
  WeatherConditions,
  | 'timestamp'
  | 'temp'
  | 'tempHigh'
  | 'tempLow'
  | 'icon'
  | 'description'
  | 'humidity'
  | 'visibility'
  | 'windDirectionCardinal'
  | 'windDirectionDegrees'
  | 'windSpeed'
>;

export type JobsiteOverviewWeatherCardProps = {
  weatherConditions: WeatherCardConditions[];
  timeZone: string | null;
  date: Moment | string;
  loading?: boolean;
};

const NOON = 12;

const HOUR_OPTIONS: { hour: number; label: string }[] = Array.from(
  { length: 24 },
  (_, hour) => ({
    hour,
    label: moment({ hour }).format('ha'),
  }),
);

const formatTemp = (value: number | null | undefined): string =>
  value == null ? '—' : `${Math.round(value)}° F`;

export function JobsiteOverviewWeatherCard({
  weatherConditions,
  timeZone,
  date,
  loading = false,
}: JobsiteOverviewWeatherCardProps): ReactElement {
  const conditionsByHour = React.useMemo(() => {
    const map = new Map<number, WeatherCardConditions>();
    weatherConditions.forEach((conditions) => {
      const localMoment = getDateTimeMoment({
        date: conditions.timestamp,
        timeZone: timeZone ?? undefined,
        isUTC: true,
      });
      if (localMoment) {
        map.set(localMoment.hour(), conditions);
      }
    });
    return map;
  }, [weatherConditions, timeZone]);

  const defaultHour = React.useMemo(() => {
    const jobsiteNow = getJobsiteMomentNow({ timeZone: timeZone ?? undefined });
    const isToday =
      moment(date).format(momentISODateFormatter) ===
      jobsiteNow.format(momentISODateFormatter);
    return isToday ? jobsiteNow.hour() : NOON;
  }, [date, timeZone]);

  const [selectedHour, setSelectedHour] = React.useState<number>(defaultHour);
  React.useEffect(() => {
    setSelectedHour(defaultHour);
  }, [defaultHour]);

  const selectedConditions = conditionsByHour.get(selectedHour);

  const { high, low } = React.useMemo(() => {
    const temps = weatherConditions
      .map((c) => c.temp)
      .filter((t): t is number => t != null);
    if (temps.length === 0) return { high: null, low: null };
    return { high: Math.max(...temps), low: Math.min(...temps) };
  }, [weatherConditions]);

  const menuItems: MenuItemProps[] = React.useMemo(
    () =>
      HOUR_OPTIONS.map(({ hour, label }) => ({
        value: hour,
        text: label,
      })),
    [],
  );

  const selectedLabel =
    HOUR_OPTIONS.find((o) => o.hour === selectedHour)?.label ?? '—';

  const highLowDisplay =
    high == null && low == null
      ? '—'
      : `${formatTemp(low)} / ${formatTemp(high)}`;

  const {
    value: isDrawerOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean(false);
  // React events bubble through the React tree, not the DOM. The Modal portals
  // its overlay to <body>, but its click events still bubble up here — so guard
  // against re-opening the drawer right after the overlay click closed it.
  const onCardClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    // The drawer renders its own "no weather data" copy for an empty array, so
    // keep the card inert until the fetch settles — otherwise a click mid-load
    // surfaces that message misleadingly.
    if (loading) return;
    if (!e.currentTarget.contains(e.target as Node)) return;
    openDrawer();
  };
  const onCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (loading) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDrawer();
    }
  };

  let weatherIcon: ReactElement;
  if (loading) {
    weatherIcon = <Shimmer className="odin-w-12 odin-h-12" />;
  } else if (selectedConditions?.icon) {
    weatherIcon = (
      <img
        src={selectedConditions.icon}
        alt={selectedConditions.description ?? 'weather'}
        width={48}
        height={48}
      />
    );
  } else {
    weatherIcon = <div className="odin-w-12 odin-h-12" />;
  }

  // Outer element is a div to preserve valid HTML hierarchy; using a button would result in the hour-picker dropdown
  // (line 146) being a nested <button> in the rendered DOM tree, which is invalid HTML
  return (
    <div
      role="button"
      tabIndex={loading ? -1 : 0}
      aria-disabled={loading}
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
      className={cn(
        'odin-flex odin-flex-col odin-gap-4',
        'odin-rounded-2xl odin-border odin-border-odin-medium-gray',
        'odin-bg-odin-white odin-p-6',
        'odin-text-left',
        loading
          ? 'odin-cursor-default'
          : 'odin-cursor-pointer odin-transition-all odin-duration-200 hover:odin-border-odin-dark-gray',
      )}
    >
      <div className="odin-flex odin-flex-row odin-items-center odin-justify-between odin-gap-3">
        <div className="odin-flex odin-flex-row odin-items-center odin-justify-center odin-gap-3">
          <span className="odin-text-sm odin-text-odin-navy">Weather</span>
          <DropdownButton
            text={selectedLabel}
            menuItems={menuItems}
            onItemSelect={(value: number): void => setSelectedHour(value)}
            size="xs"
            theme="white"
            popupWidthFitContent
          />
        </div>
        <ChevronRightIcon className="odin-w-3 odin-h-3 odin-shrink-0 odin-text-odin-navy" />
      </div>
      <div className="odin-flex odin-items-center odin-gap-6">
        <div className="odin-flex odin-items-center odin-grow odin-gap-3 odin-min-w-0">
          {weatherIcon}
          <div className="odin-flex odin-flex-col odin-gap-0.5 odin-min-w-0">
            {loading ? (
              <Shimmer className="odin-h-6 odin-w-16 odin-my-0.5" />
            ) : (
              <span className="odin-text-lg odin-font-semibold odin-text-odin-navy">
                {formatTemp(selectedConditions?.temp)}
              </span>
            )}
            <span className="odin-text-sm odin-text-odin-navy">
              At {moment({ hour: selectedHour }).format('ha')}
            </span>
          </div>
        </div>
        <div className="odin-flex odin-flex-row odin-grow odin-justify-start">
          <div className="odin-flex odin-flex-col odin-gap-0.5 odin-items-start">
            {loading ? (
              <Shimmer className="odin-h-6 odin-w-24 odin-my-0.5" />
            ) : (
              <span className="odin-text-lg odin-font-semibold odin-text-odin-navy">
                {highLowDisplay}
              </span>
            )}
            <span className="odin-text-sm odin-text-odin-navy">Low / High</span>
          </div>
        </div>
      </div>
      <JobsiteOverviewWeatherDrawer
        isOpen={isDrawerOpen}
        closeDrawer={closeDrawer}
        weatherConditions={weatherConditions}
        timeZone={timeZone}
      />
    </div>
  );
}
